<template>
  <div class="ask-input-demo-page">
    <div class="demo-card">
      <div class="config-panel">
        <div class="panel-title">模型配置</div>

        <el-form label-position="top" @submit.native.prevent>
          <el-form-item label="来源">
            <div class="model-row">
              <el-select
                v-model="source"
                style="flex: 1"
                filterable
                placeholder="请选择来源"
                :loading="sourceLoading"
                @change="handleSourceChange"
              >
                <el-option
                  v-for="item in sourceOptions"
                  :key="item.source"
                  :label="item.source"
                  :value="item.source"
                >
                  <span class="model-config-source-option">
                    <span class="model-config-source-option__name">{{ item.source }}</span>
                    <span
                      class="model-config-source-tag"
                      :class="statusClass(item)"
                    >
                      <i></i>
                      {{ statusText(item) }}
                    </span>
                  </span>
                </el-option>
              </el-select>
              <el-button
                size="small"
                :loading="sourceTesting"
                :disabled="!source"
                @click="handleTestSource"
              >测试</el-button>
            </div>
          </el-form-item>

          <el-form-item label="模型">
            <div class="model-row">
              <el-select
                v-model="model"
                style="flex: 1"
                filterable
                placeholder="请先选择来源"
                :loading="modelLoading"
                :disabled="!source"
                @change="persistConfig"
              >
                <el-option
                  v-for="item in modelOptions"
                  :key="item"
                  :label="item"
                  :value="item"
                />
              </el-select>
              <el-button
                size="small"
                :loading="modelLoading"
                :disabled="!source"
                @click="fetchModels"
              >刷新模型</el-button>
            </div>
          </el-form-item>

          <el-form-item label="系统提示词">
            <el-input
              v-model.trim="systemPrompt"
              type="textarea"
              :rows="3"
              maxlength="300"
              show-word-limit
              placeholder="可选，用于控制回答风格"
              @input="persistConfig"
            />
          </el-form-item>
        </el-form>

        <div class="config-tip">
          <p>1. 来源取自「第三方模型」模块已接入的列表。</p>
          <p>2. 模型下拉会在选定来源后自动拉取该来源的全部模型。</p>
          <p>3. 接口地址与密钥随来源自动带入，无需手工填写。</p>
        </div>
      </div>

      <div class="workspace-panel">
        <div class="question-panel">
          <div class="panel-title">问答输入</div>

          <el-input
            v-model.trim="question"
            type="textarea"
            resize="none"
            :rows="6"
            maxlength="1000"
            show-word-limit
            placeholder="请输入你的问题，例如：请用简单例子解释一下 Vue2 中 computed 和 watch 的区别。"
            @keydown.ctrl.enter.native="submitQuestion"
          />

          <div class="question-actions">
            <el-button @click="clearAll">清空</el-button>
            <el-button v-if="loading" type="danger" plain @click="cancelRequest">停止</el-button>
            <el-button type="primary" :loading="loading" @click="submitQuestion">
              {{ loading ? '生成中...' : '开始提问' }}
            </el-button>
          </div>
        </div>

        <div class="stream-panel">
          <div class="stream-header">
            <span class="panel-title">AI 返回信息流</span>
            <el-button type="text" :disabled="!answerStream" @click="copyAnswer">复制结果</el-button>
          </div>

          <div ref="streamContainer" class="stream-output">
            <div v-if="answerStream" class="stream-content">{{ answerStream }}</div>
            <div v-else class="stream-placeholder">这里会展示模型流式返回的内容。</div>
          </div>

          <div v-if="errorMessage" class="error-box">{{ errorMessage }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  fetchThirdPartyModelList,
  fetchThirdPartyModelModelsBySource,
  testThirdPartyModelBySource,
  buildThirdPartyChatUrl
} from '../../../api/modules/thirdPartyModel'

// 页面本地缓存 Key，用于保存模型配置。
const STORAGE_KEY = 'openai_ask_input_demo_config'

export default {
  name: 'OpenAiAskInputDemo',
  data() {
    return {
      // 第三方模型来源列表（取自「第三方模型」模块右侧列表）。
      sourceOptions: [],
      // 来源列表加载状态。
      sourceLoading: false,
      // 当前选中的来源名称。
      source: '',
      // 来源测试进行中。
      sourceTesting: false,
      // 当前选中的模型名称。
      model: '',
      // 模型下拉可选项。
      modelOptions: [],
      // 模型列表加载状态。
      modelLoading: false,
      // 系统提示词，用于约束模型回答风格。
      systemPrompt: '你是一个清晰、可靠、简洁的中文 AI 助手。',
      // 用户当前输入的问题。
      question: '',
      // 流式拼接后的回答文本。
      answerStream: '',
      // 当前问答请求状态。
      loading: false,
      // 当前错误提示内容。
      errorMessage: '',
      // 流式请求中止控制器。
      abortController: null
    }
  },
  created() {
    // 页面创建时恢复本地缓存，并拉取来源列表。
    this.restoreConfig()
    this.fetchSources()
  },
  methods: {
    /**
     * 拉取第三方模型来源列表，作为「来源」下拉的数据源。
     */
    async fetchSources() {
      this.sourceLoading = true
      try {
        const data = await fetchThirdPartyModelList()
        this.sourceOptions = (data && Array.isArray(data.list)) ? data.list : []

        // 缓存的来源已被删除时，清空选择，避免停留在无效来源上。
        if (this.source && !this.sourceOptions.some(item => item.source === this.source)) {
          this.source = ''
          this.model = ''
          this.modelOptions = []
        }
      } catch (error) {
        // 拦截器已统一提示错误
      } finally {
        this.sourceLoading = false
      }
    },

    /**
     * 来源可用状态文案。
     * @param {Object} row 来源记录
     * @returns {string}
     */
    statusText(row) {
      if (row.available === 1) return '可用'
      if (row.available === 0) return '不可用'
      return '未测试'
    },

    /**
     * 来源可用状态样式类。
     * @param {Object} row 来源记录
     * @returns {string}
     */
    statusClass(row) {
      if (row.available === 1) return 'is-success'
      if (row.available === 0) return 'is-danger'
      return 'is-unknown'
    },

    /**
     * 根据选中的来源取出对应记录。
     * @returns {Object|null}
     */
    currentSourceRecord() {
      return this.sourceOptions.find(item => item.source === this.source) || null
    },

    /**
     * 切换来源：清空模型并自动拉取该来源的全部模型。
     */
    handleSourceChange() {
      this.model = ''
      this.modelOptions = []
      this.persistConfig()
      this.fetchModels()
    },

    /**
     * 测试当前来源是否可用，测试完刷新列表以更新状态色块。
     */
    async handleTestSource() {
      if (!this.source || this.sourceTesting) return

      this.sourceTesting = true
      try {
        const record = await testThirdPartyModelBySource(this.source)
        this.$toast[record && record.available ? 'success' : 'warning'](
          `${this.source}：${(record && record.testMessage) || '测试完成'}`
        )
        await this.fetchSources()
        // 测试会顺带刷新该来源的模型清单，同步更新模型下拉。
        await this.fetchModels()
      } catch (error) {
        // 拦截器已统一提示错误
      } finally {
        this.sourceTesting = false
      }
    },

    // 从本地缓存恢复模型配置。
    restoreConfig() {
      try {
        const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null')
        if (!parsed) return
        this.source = parsed.source || ''
        this.model = parsed.model || ''
        this.systemPrompt = parsed.systemPrompt || this.systemPrompt
      } catch (error) {
        console.warn('Failed to restore askInputDemo config:', error)
      }
    },

    // 将当前配置写入 localStorage，方便刷新页面后继续使用。
    persistConfig() {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          source: this.source,
          model: this.model,
          systemPrompt: this.systemPrompt
        })
      )
    },

    // 拉取当前来源包含的全部模型，并同步默认模型。
    async fetchModels() {
      if (!this.source) {
        this.$toast.warning('请先选择来源')
        return
      }

      this.modelLoading = true
      try {
        // 点「刷新模型」时强制实时重新拉取一次。
        const data = await fetchThirdPartyModelModelsBySource(this.source, true)
        const names = (data && Array.isArray(data.models)) ? data.models : []

        this.modelOptions = names
        if (!this.modelOptions.length) {
          throw new Error((data && data.message) || '该来源没有返回可用模型')
        }

        if (!this.modelOptions.includes(this.model)) {
          this.model = this.modelOptions[0]
        }

        this.persistConfig()
      } catch (error) {
        console.error('Failed to fetch model list:', error)
        this.errorMessage = error.message || '模型列表获取失败'
        this.$toast.error(this.errorMessage)
      } finally {
        this.modelLoading = false
      }
    },

    // 发送流式问答请求，并持续读取 SSE 增量数据。
    async submitQuestion() {
      if (this.loading) return

      const sourceRecord = this.currentSourceRecord()
      if (!this.source || !sourceRecord) {
        this.$toast.warning('请先选择来源')
        return
      }
      if (!this.model) {
        this.$toast.warning('请先选择模型')
        return
      }
      if (!this.question) {
        this.$toast.warning('请输入你的问题')
        return
      }

      this.persistConfig()
      this.answerStream = ''
      this.errorMessage = ''
      this.loading = true
      this.abortController = new AbortController()

      try {
        // 走本服务端代理转发：浏览器直连第三方站点会被 CORS 拦截。
        const response = await fetch(buildThirdPartyChatUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            source: sourceRecord.source,
            model: this.model,
            stream: true,
            messages: [
              {
                role: 'system',
                content: this.systemPrompt || '你是一个清晰、可靠、简洁的中文 AI 助手。'
              },
              {
                role: 'user',
                content: this.question
              }
            ]
          }),
          signal: this.abortController.signal
        })

        if (!response.ok) {
          const errorText = await response.text()
          throw new Error(this.parseErrorMessage(errorText) || `请求失败：${response.status}`)
        }

        if (!response.body || !window.TextDecoder) {
          throw new Error('当前浏览器不支持流式响应读取')
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder('utf-8')
        let buffer = ''

        while (true) {
          const { value, done } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''
          lines.forEach(line => this.processStreamLine(line))
        }

        if (buffer) {
          this.processStreamLine(buffer)
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          this.$toast.info('已停止生成')
        } else {
          console.error('Ask input demo request failed:', error)
          this.errorMessage = error.message || '请求失败，请稍后重试'
          this.$toast.error(this.errorMessage)
        }
      } finally {
        this.loading = false
        this.abortController = null
      }
    },

    // 解析单行 SSE 数据，只提取模型增量返回的 content 字段。
    processStreamLine(line) {
      const trimmedLine = String(line || '').trim()
      if (!trimmedLine || !trimmedLine.startsWith('data:')) return

      const payload = trimmedLine.replace(/^data:\s*/, '')
      if (payload === '[DONE]') return

      try {
        const parsed = JSON.parse(payload)

        // 上游限流 / 额度不足等错误会以流内 error 事件下发，直接展示给用户。
        if (parsed.error) {
          const message = typeof parsed.error === 'string'
            ? parsed.error
            : (parsed.error.message || '上游返回错误')

          if (message) {
            this.errorMessage = message
            this.$toast.error(message)
          }
          return
        }

        const delta = parsed.choices && parsed.choices[0] && parsed.choices[0].delta
        if (delta && delta.content) {
          this.answerStream += delta.content
          this.scrollStreamToBottom()
        }
      } catch (error) {
        console.warn('Failed to parse stream line:', trimmedLine, error)
      }
    },

    // 尝试从接口错误响应中提取更直观的 message。
    parseErrorMessage(text) {
      try {
        const parsed = JSON.parse(text)
        return parsed.error && parsed.error.message
      } catch (error) {
        return text
      }
    },

    // 自动将输出区滚动到底部，方便观察连续返回的内容。
    scrollStreamToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.streamContainer
        if (!container) return
        container.scrollTop = container.scrollHeight
      })
    },

    // 中断当前请求。
    cancelRequest() {
      if (this.abortController) {
        this.abortController.abort()
      }
    },

    // 清空问题、回答和错误状态。
    clearAll() {
      this.question = ''
      this.answerStream = ''
      this.errorMessage = ''
    },

    // 复制当前已返回的回答结果。
    async copyAnswer() {
      if (!this.answerStream) {
        this.$toast.warning('当前没有可复制的内容')
        return
      }

      try {
        await navigator.clipboard.writeText(this.answerStream)
        this.$toast.success('复制成功')
      } catch (error) {
        this.$toast.error('复制失败')
      }
    }
  }
}
</script>

<style scoped>
.ask-input-demo-page {
  /* 撑满内容区，滚动下沉到模块内部 */
  height: 100%;
  min-height: 0;
  padding: 0;
  box-sizing: border-box;
  overflow: auto;
  background: transparent;
}

/* 去掉 .page-header 后，内容区（.content-wrapper）统一提供 12px 内边距。 */

.demo-card {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 18px;
  align-items: start;
}

.config-panel,
.question-panel,
.stream-panel {
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 16px;
  background: var(--surface-1);
  box-shadow: 0 10px 28px rgba(31, 45, 61, 0.06);
}

.config-panel {
  padding: 18px;
}

.workspace-panel {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.question-panel,
.stream-panel {
  padding: 18px;
}

.panel-title {
  margin-bottom: 14px;
  color: #22324a;
  font-size: 16px;
  font-weight: 600;
}

.model-row {
  display: flex;
  gap: 10px;
}

.config-tip {
  padding: 14px 16px;
  border-radius: 14px;
  background: linear-gradient(180deg, #f7fafc 0%, #eef4fb 100%);
  color: #5d6b7b;
  line-height: 1.8;
  font-size: 13px;
}

.config-tip p {
  margin: 0;
}

.question-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 14px;
}

.stream-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
}

.stream-output {
  min-height: 320px;
  max-height: 520px;
  overflow-y: auto;
  padding: 16px;
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 14px;
  background: linear-gradient(180deg, #f9fbff 0%, #eff5fb 100%);
}

.stream-content {
  white-space: pre-wrap;
  word-break: break-word;
  color: #243447;
  line-height: 1.8;
}

.stream-placeholder {
  color: #8b98a8;
  line-height: 1.8;
}

.error-box {
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fff4f4;
  border: 1px solid #ffd7d5;
  color: #d93026;
  line-height: 1.6;
}

@media (max-width: 1100px) {
  .demo-card {
    grid-template-columns: 1fr;
  }
}
</style>

<!--
  el-option 的内容会被渲染到 body 上的下拉浮层里，scoped 样式对它无效，
  这里用非 scoped 样式补一份，保证选项右侧的状态色块能正常显示。
-->
<style>
.model-config-source-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.model-config-source-option .model-config-source-option__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.model-config-source-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex: 0 0 auto;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
}

.model-config-source-tag i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.model-config-source-tag.is-success {
  color: #17a25b;
  background: #e8f7ef;
}

.model-config-source-tag.is-danger {
  color: #d93026;
  background: #fdeceb;
}

.model-config-source-tag.is-unknown {
  color: #8b98a8;
  background: var(--surface-2);
}
</style>
